import { useState, useEffect, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import { FormModal } from './FormModal';
import { TextInput, AutocompleteInput, CheckboxInput } from '../inputs';
import {
  useDispatch,
  addResource,
  getCarrierList,
  getCarrierServiceByCodeList,
} from '../../../state';
import { IdNamePair } from '../../../types';

interface Props {
  open: boolean;
  handleClose: () => void;
  orderId: number;
  onSuccess?: () => void;
}

interface FormState {
  carrierEntryMode: 'existing' | 'manual';
  carrierCode: string;
  carrierServiceCode: string;
  trackingNumber: string;
}

const initialState: FormState = {
  carrierEntryMode: 'existing',
  carrierCode: '',
  carrierServiceCode: '',
  trackingNumber: '',
};

export function LabelTrackingForm({
  open,
  handleClose,
  orderId,
  onSuccess,
}: Props) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [carrierList, setCarrierList] = useState<IdNamePair[]>([]);
  const [servicesList, setServicesList] = useState<IdNamePair[]>([]);

  useEffect(() => {
    if (!open) return;
    (async function () {
      const { data } = await dispatch(
        getCarrierList({ includeManualCarriers: true }),
      );
      if (data) {
        setCarrierList(data);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchServices = useCallback(
    async (carrierCode: string) => {
      if (!carrierCode) {
        setServicesList([]);
        return;
      }
      const { data } = await dispatch(
        getCarrierServiceByCodeList(carrierCode, {
          includeAllAccounts: true,
        }),
      );
      if (data) {
        setServicesList(data);
      }
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async (values: FormState) => {
      setSubmitting(true);
      const { error } = await dispatch(
        addResource({
          baseUrl: `/orders/${orderId}/label-tracking`,
          payload: {
            carrierCode: values.carrierCode || null,
            carrierServiceCode: values.carrierServiceCode || null,
            trackingNumber: values.trackingNumber || null,
          },
          message: 'Label tracking info saved',
        }),
      );
      setSubmitting(false);
      if (!error) {
        onSuccess?.();
        handleClose();
      }
    },
    [orderId, dispatch, handleClose, onSuccess],
  );

  const formik = useFormik({
    initialValues: initialState,
    enableReinitialize: false,
    onSubmit: handleSubmit,
  });

  const handleCarrierEntryModeChange = useCallback(
    (e) => {
      const nextMode = (e.target as HTMLInputElement).checked
        ? 'manual'
        : 'existing';
      formik.setFieldValue('carrierEntryMode', nextMode);
      formik.setFieldValue('carrierCode', '');
      formik.setFieldValue('carrierServiceCode', '');
      setServicesList([]);
    },
    [formik],
  );

  const handleCarrierChange = useCallback(
    async (e) => {
      const { value } = e.target;
      formik.setFieldValue('carrierCode', value);
      formik.setFieldValue('carrierServiceCode', '');
      await fetchServices(value);
    },
    [formik, fetchServices],
  );

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={
        <span className={classes.title}>Label tracking info</span>
      }
      callback={formik.handleSubmit}
      btnText='SAVE'
      cancelBtnText='SKIP'
      typeSubmit
      paperProps={{ style: { width: 520 } }}
      submitting={submitting}
    >
      <div className={classes.subtitle}>
        Enter carrier and tracking info for this prepaid label (all fields are
        optional).
      </div>
      <Grid container spacing={2}>
        <Grid size={12}>
          <CheckboxInput
            formikProps={formik}
            useCustomValue
            checkboxes={[
              {
                labelProps: { label: 'Enter carrier manually' },
                checkboxProps: {
                  name: 'carrierEntryMode',
                  checked: formik.values.carrierEntryMode === 'manual',
                  onChange: handleCarrierEntryModeChange,
                },
              },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {formik.values.carrierEntryMode === 'manual' ? (
            <TextInput
              name='carrierCode'
              formikProps={formik}
              label='Carrier'
            />
          ) : (
            <AutocompleteInput
              formikProps={formik}
              textFieldProps={{
                name: 'carrierCode',
                label: 'Carrier',
                autoComplete: 'new-password',
                onChange: handleCarrierChange,
              }}
              autocompleteProps={{
                options: carrierList,
              }}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {formik.values.carrierEntryMode === 'manual' ? (
            <TextInput
              name='carrierServiceCode'
              formikProps={formik}
              label='Service'
            />
          ) : (
            <AutocompleteInput
              formikProps={formik}
              textFieldProps={{
                name: 'carrierServiceCode',
                label: 'Service',
                autoComplete: 'new-password',
              }}
              autocompleteProps={{
                options: servicesList,
              }}
            />
          )}
        </Grid>
        <Grid size={12}>
          <TextInput
            name='trackingNumber'
            formikProps={formik}
            label='Tracking number'
          />
        </Grid>
      </Grid>
    </FormModal>
  );
}

const useStyles = makeStyles({ name: { LabelTrackingForm } })((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  subtitle: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
}));
